import { getLevelFromXP } from '@blind-kings/game-core';
import { api } from 'aws-blocks';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import UpdateUserModal, { USER_FIELD_VALUES } from '../../Components/UpdateUserModal';
import BkPanel from '../../Components/ui/BkPanel';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSnackBarContext } from '../../contexts/SnackBarContext';
import type { UserData } from '../../types/app';
import { resolveOnlineGameId } from '../../utils/active-games';
import usePageMeta from '../../hooks/use-page-meta';
import MatchHistory from './MatchHistory';
import ProfileAvatarPicker from './ProfileAvatarPicker';
import ProfileEmotes from './ProfileEmotes';
import ProfileHero from './ProfileHero';
import ProfileNotFound from './ProfileNotFound';
import ProfileOverview from './ProfileOverview';
import ProfilePreferences from './ProfilePreferences';
import ProfileSkins from './ProfileSkins';
import ProfileTabs from './ProfileTabs';
import { parseProfileTab, type ProfileTab } from './profile-tabs';

export type ProfileData = UserData & {
  pk?: string;
  wins?: number;
  losses?: number;
  gamesPlayed?: number;
  games?: Record<string, unknown>[];
  nextUpdateNameAt?: number;
};

export default function Profile() {
  const { userId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userData, currentUser, openLoginModal } = useAuthContext();
  const [profile, setProfile] = useState<ProfileData | undefined>();
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const { addSnackBar } = useSnackBarContext();

  const username = userId?.toLowerCase();
  const isOwnProfile = userData?.displayName?.toLowerCase() === username;
  const tab: ProfileTab = parseProfileTab(searchParams.get('tab'), !!isOwnProfile);

  usePageMeta({
    title: profile?.displayName
      ? `${profile.displayName} — Profile`
      : 'Profile',
    description: 'Blind Kings player profile, match history, skins, and emotes.',
    path: `/user/${userId || ''}`,
    noindex: true,
  });

  useEffect(() => {
    const raw = searchParams.get('tab');
    const normalized = parseProfileTab(raw, !!isOwnProfile);
    if (raw !== normalized) {
      setSearchParams({ tab: normalized }, { replace: true });
    }
  }, [searchParams, isOwnProfile, setSearchParams]);

  const getUserProfile = useCallback(async () => {
    if (!username) return;
    try {
      const data = (await api.getProfile(username)) as ProfileData | null;
      if (!data) {
        setProfile({});
        return;
      }
      setProfile({
        ...data,
        level: getLevelFromXP((data.xp as number) || 0),
        games: (data.games as Record<string, unknown>[]) || [],
      });
    } catch {
      addSnackBar({
        title: 'ERROR',
        message: 'Error getting player info. Please try again later.',
        variant: 'danger',
        timeout: 2500,
      });
      setProfile({});
    }
  }, [username, addSnackBar]);

  useEffect(() => {
    if (!username) return;
    if (isOwnProfile) setProfile(userData);
    else {
      setProfile(undefined);
      void getUserProfile();
    }
  }, [username, userData, isOwnProfile, getUserProfile]);

  const inMatch =
    isOwnProfile && Boolean(resolveOnlineGameId(userData));

  function renderTab() {
    if (!profile) return null;
    switch (tab) {
      case 'history':
        return (
          <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-10">
            <MatchHistory games={profile.games as never} compact />
          </div>
        );
      case 'skins':
        return isOwnProfile ? <ProfileSkins /> : null;
      case 'emotes':
        return isOwnProfile ? <ProfileEmotes /> : null;
      case 'preferences':
        return isOwnProfile ? <ProfilePreferences /> : null;
      case 'overview':
      default:
        return (
          <ProfileOverview profile={profile} isOwnProfile={!!isOwnProfile} />
        );
    }
  }

  return (
    <div className="flex w-full flex-col">
      {profile === undefined ? (
        <div className="flex flex-1 items-center justify-center px-6 py-12">
          <BkPanel className="w-full max-w-xl text-center">
            <h1 className="text-2xl font-bold uppercase tracking-wider text-bk-gold animate-bk-pulse-soft sm:text-4xl">
              Loading Profile
            </h1>
          </BkPanel>
        </div>
      ) : profile.displayName ? (
        <>
          <ProfileAvatarPicker
            show={showAvatarPicker}
            onClose={() => setShowAvatarPicker(false)}
          />
          <UpdateUserModal
            show={showRename}
            onClose={() => setShowRename(false)}
            fields={[USER_FIELD_VALUES.DISPLAY_NAME]}
            onComplete={({ displayName: newDisplayName }) => {
              setShowRename(false);
              if (newDisplayName) {
                window.location.assign(
                  `/user/${newDisplayName}?tab=${tab}`
                );
              }
            }}
          />
          <ProfileHero
            profile={profile}
            isOwnProfile={!!isOwnProfile}
            inMatch={inMatch}
            compact={tab !== 'overview'}
            onChangeAvatar={
              isOwnProfile
                ? () => {
                    if (!currentUser) openLoginModal();
                    else setShowAvatarPicker(true);
                  }
                : undefined
            }
            onEditName={
              isOwnProfile
                ? () => {
                    const next = profile.nextUpdateNameAt;
                    if (next !== undefined && next >= Date.now()) {
                      addSnackBar({
                        title: 'Locked',
                        message: `You can rename again on ${new Date(next).toLocaleDateString()}.`,
                        variant: 'warning',
                        timeout: 2500,
                      });
                      return;
                    }
                    setShowRename(true);
                  }
                : undefined
            }
          />
          <ProfileTabs
            tab={tab}
            isOwnProfile={!!isOwnProfile}
            displayName={String(profile.displayName)}
          />
          <div className="min-h-0 flex-1">{renderTab()}</div>
        </>
      ) : (
        <ProfileNotFound userId={userId} />
      )}
    </div>
  );
}
